import { useState } from "react";
import { BookOpen, Layers, Video, Users, UserCheck, UserX, CreditCard } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function AdminDashboard({ stats, onNavigate }) {
  const [resetting, setResetting] = useState(false);

  const handleReset = async () => {
    if (!window.confirm("Réinitialiser TOUTES les données ? Membres, paiements, accès, témoignages seront supprimés. Cette action est irréversible.")) return;
    if (!window.confirm("Êtes-vous vraiment sûr ? Dernière confirmation.")) return;
    setResetting(true);
    try {
      await api.post("/admin/maintenance/reset-data");
      toast.success("Données réinitialisées — la base est propre.");
      window.location.reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setResetting(false);
    }
  };

  const card = (label, value, Icon, testid) => (
    <div className="bg-surface border border-ink/10 p-6" data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-[0.25em] text-ink/40">{label}</p>
        <Icon size={16} className="text-ember" />
      </div>
      <p className="mt-3 font-serif italic text-4xl text-ink">{value ?? "—"}</p>
    </div>
  );

  const shortcut = (label, tab, testid) => (
    <button
      data-testid={testid}
      onClick={() => onNavigate(tab)}
      className="border border-ink/15 hover:border-ember hover:text-ember text-ink/70 px-5 py-3.5 text-sm transition-colors text-left"
    >
      {label}
    </button>
  );

  return (
    <div className="mt-10" data-testid="admin-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {card("Utilisateurs", stats?.users, Users, "stat-users")}
        {card("Avec accès", stats?.with_access, UserCheck, "stat-with-access")}
        {card("Sans accès", stats?.without_access, UserX, "stat-without-access")}
        {card("Paiements en attente", stats?.pending_payments, CreditCard, "stat-pending-payments")}
        {card("Formations", stats?.courses, BookOpen, "stat-courses")}
        {card("Modules", stats?.modules, Layers, "stat-modules")}
        {card("Vidéos", stats?.videos, Video, "stat-videos")}
      </div>

      <h2 className="mt-12 mb-5 text-xs uppercase tracking-[0.25em] text-ink/40">Raccourcis</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {shortcut("Ajouter une vidéo", "formations", "shortcut-add-video")}
        {shortcut("Gérer les formations", "formations", "shortcut-courses")}
        {shortcut("Gérer les utilisateurs", "users", "shortcut-users")}
        {shortcut("Gérer les accès", "users", "shortcut-access")}
        {shortcut("Paiements manuels", "payments", "shortcut-payments")}
      </div>

      <div className="mt-16 border border-red-200 bg-red-50/50 p-6" data-testid="danger-zone">
        <h2 className="text-xs uppercase tracking-[0.25em] text-red-500">Zone de maintenance</h2>
        <p className="mt-2 text-sm text-ink/60 max-w-2xl">
          Réinitialiser les données : supprime tous les membres (sauf votre compte admin), accès, paiements,
          commandes, témoignages, progressions et les fichiers de test. La structure de la formation est conservée.
          Action irréversible.
        </p>
        <button
          data-testid="reset-data-btn"
          onClick={handleReset}
          disabled={resetting}
          className="mt-4 border border-red-400 text-red-600 hover:bg-red-500 hover:text-white disabled:opacity-50 px-5 py-3 text-sm transition-colors"
        >
          {resetting ? "Réinitialisation…" : "Réinitialiser les données"}
        </button>
      </div>
    </div>
  );
}
